import { ScreenType, useScreenType } from "@redotech/react-util/screen";
import { AutoClaimsUpsellModal } from "@redotech/redo-merchant-fulfillment-app/common/shipping-claim/auto-claims-upsell-modal";
import { FeatureOnboardingType } from "@redotech/redo-model/onboarding/user-onboarding";
import { assertNever } from "@redotech/util/type";
import { memo, useMemo } from "react";
import { useLocation } from "react-router-dom";
import * as styles from "./index.module.css";
import { AgenticCatalogOnboarding } from "./onboarding-cards/agentic-catalog-onboarding";
import { BillingSettingsConsolidatedOnboarding } from "./onboarding-cards/billing-settings-consolidated";
import { ConversionOnboarding } from "./onboarding-cards/conversion-onboarding";
import { ExpandedWarrantyOnboarding } from "./onboarding-cards/expanded-warranty-onboarding";
import { HelpIconMovingOnboarding } from "./onboarding-cards/help-icon-moving";
import { IntercomMovedOnboarding } from "./onboarding-cards/intercom-moved";
import { ManagedReturnsOnboarding } from "./onboarding-cards/managed-returns";
import { PackageProtectionPlusOnboarding } from "./onboarding-cards/package-protection-plus";
import { ReclaimAnnouncementOnboarding } from "./onboarding-cards/reclaim-announcement";
import { RedoCommentsold } from "./onboarding-cards/redo-commentsold";
import { AiSupportActionNeededV2Modal } from "./onboarding-modals/ai-support-action-needed-v2-modal";
import { HomepageAnalyticsMovedModal } from "./onboarding-modals/homepage-analytics-moved-modal";
import { NavReorganizationModal } from "./onboarding-modals/nav-reorganization-modal";
import { OutboundLabelsUpsellOnboarding } from "./onboarding-modals/outbound-labels-upsell";
import { ShopperAiWrappedModal } from "./onboarding-modals/shopper-ai-wrapped-modal";
import { useFeatureOnboardingManager } from "./use-feature-onboarding";

export const FeatureOnboarding = memo(function FeatureOnboarding() {
  const screenType = useScreenType();
  const {
    onboardingType: managerType,
    showOnboarding: managerShow,
    handleDismiss,
    handleComplete,
  } = useFeatureOnboardingManager();

  // Dev affordance: `?onboarding-preview=<TYPE>` force-renders any onboarding
  // card so designers/devs can verify positioning and styling without having
  // to satisfy the eligibility logic. No-op unless the param is present.
  const previewType = useOnboardingPreviewType();
  const onboardingType = previewType ?? managerType;
  const showOnboarding = previewType ? true : managerShow;

  const card = useMemo(() => {
    if (screenType === ScreenType.MOBILE) {
      return null;
    }
    if (
      !onboardingType ||
      !showOnboarding ||
      onboardingType === FeatureOnboardingType.DISABLED
    ) {
      return null;
    }
    switch (onboardingType) {
      case FeatureOnboardingType.PACKAGE_PROTECTION_PLUS:
        return (
          <PackageProtectionPlusOnboarding
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.EXPANDED_WARRANTY:
        return (
          <ExpandedWarrantyOnboarding
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.MANAGED_RETURNS:
        return (
          <ManagedReturnsOnboarding
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.CONVERSION:
        return (
          <ConversionOnboarding
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.INTERCOM_MOVED:
        return (
          <IntercomMovedOnboarding
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.SUPPORT_TO_AI_UPSELL:
        return (
          <AiSupportActionNeededV2Modal
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.COMMENTSOLD_REDO:
        return (
          <RedoCommentsold
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.SHOPPER_AI_WRAPPED:
        return (
          <ShopperAiWrappedModal
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.BILLING_SETTINGS_CONSOLIDATED:
        return (
          <BillingSettingsConsolidatedOnboarding
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.HELP_ICON_MOVING:
        return (
          <HelpIconMovingOnboarding
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.AGENTIC_CATALOG:
        return (
          <AgenticCatalogOnboarding
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.OUTBOUND_LABELS_UPSELL_UPS:
      case FeatureOnboardingType.OUTBOUND_LABELS_UPSELL_AMAZON:
      case FeatureOnboardingType.OUTBOUND_LABELS_UPSELL_DHL:
        return (
          <OutboundLabelsUpsellOnboarding
            carrier={onboardingType}
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.HOMEPAGE_ANALYTICS_MOVED:
        return (
          <HomepageAnalyticsMovedModal
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.NAV_REORGANIZATION:
        return (
          <NavReorganizationModal
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.RECLAIM_ANNOUNCEMENT:
        return (
          <ReclaimAnnouncementOnboarding
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.SHIPPING_CLAIMS_AUTO_FILING:
        return (
          <AutoClaimsUpsellModal
            onAccept={handleComplete}
            onClose={handleDismiss}
            open
          />
        );
      // Historical onboarding types - no longer actively used
      case FeatureOnboardingType.CROSS_BORDER_ONBOARDING:
      case FeatureOnboardingType.OUTBOUND_VIEWS_MOVED:
      case FeatureOnboardingType.DISPUTES_WELCOME:
      case FeatureOnboardingType.AI_EXCHANGE_AGENT_UPSELL:
      case FeatureOnboardingType.INTERNATIONAL_RETURNS:
      case FeatureOnboardingType.ROLES_PERMISSIONS_UPDATE:
      case FeatureOnboardingType.MARKETING_TEXAS_SMS_PROTECTION:
      case FeatureOnboardingType.BLACK_FRIDAY_TIPS:
      case FeatureOnboardingType.PERMISSIONS_ROLES:
      case FeatureOnboardingType.OUTBOUND_LABELS:
        return null;
      default:
        assertNever(onboardingType);
    }
  }, [
    handleComplete,
    handleDismiss,
    onboardingType,
    showOnboarding,
    screenType,
  ]);

  if (!card) {
    return null;
  }

  return <div className={styles.container}>{card}</div>;
});

export const CROSS_SELL_POPUP_URL =
  "https://assets.getredo.com/cross-sell-popup";

function useOnboardingPreviewType(): FeatureOnboardingType | null {
  const { search } = useLocation();
  return useMemo(() => {
    const raw = new URLSearchParams(search).get("onboarding-preview");
    if (!raw) {
      return null;
    }
    // Accept both enum keys (e.g. "MANAGED_RETURNS") and values
    // (e.g. "managed_returns") so query strings can use either form.
    const enumObj = FeatureOnboardingType as Record<string, string>;
    if (raw in enumObj) {
      return enumObj[raw] as FeatureOnboardingType;
    }
    const valueSet = new Set<string>(Object.values(enumObj));
    return valueSet.has(raw) ? (raw as FeatureOnboardingType) : null;
  }, [search]);
}
